
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface StudentActivity {
  id: string;
  activityId: string;
  activityName: string;
  requiresDaySelection: boolean;
  scheduledDays: number[];
  status: string;
  availableBillingTypes: ('per_session' | 'monthly')[];
  billingTypeOverride?: 'per_session' | 'monthly' | null;
}

export const useStudentActivities = (studentId?: string) => {
  const [activities, setActivities] = useState<StudentActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadStudentActivities = async () => {
    if (!studentId) return;
    
    setLoading(true);
    try {
      // Get student activities with activity details and billing type override
      const { data: studentActivities, error: activitiesError } = await supabase
        .from('student_activities')
        .select(`
          id,
          activity_id,
          status,
          billing_type_override,
          activities!inner (
            name,
            requires_day_selection
          )
        `)
        .eq('student_id', studentId)
        .eq('status', 'active');

      if (activitiesError) throw activitiesError;

      // Get available billing types for each activity from activity_prices
      const activitiesData: StudentActivity[] = [];
      
      for (const sa of studentActivities || []) {
        const activity = sa.activities as any;
        let scheduledDays: number[] = [];
        
        // Get scheduled days for activities that require day selection
        if (activity.requires_day_selection) {
          const { data: scheduleData, error: scheduleError } = await supabase
            .from('student_activity_schedule')
            .select('day_of_week')
            .eq('student_id', studentId)
            .eq('activity_id', sa.activity_id);

          if (scheduleError) throw scheduleError;
          scheduledDays = scheduleData?.map(s => s.day_of_week) || [];
        }

        // Get available billing types from activity_prices
        const { data: pricesData, error: pricesError } = await supabase
          .from('activity_prices')
          .select('billing_type_target')
          .eq('activity_id', sa.activity_id)
          .eq('is_active', true);

        if (pricesError) throw pricesError;

        const availableBillingTypes = [...new Set(pricesData?.map(p => p.billing_type_target) || [])];

        activitiesData.push({
          id: sa.id,
          activityId: sa.activity_id,
          activityName: activity.name,
          requiresDaySelection: activity.requires_day_selection,
          scheduledDays,
          status: sa.status,
          availableBillingTypes,
          billingTypeOverride: sa.billing_type_override
        });
      }

      setActivities(activitiesData);
    } catch (error) {
      
      toast({
        title: "Error",
        description: "No se pudieron cargar las actividades del estudiante",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateScheduledDays = async (activityId: string, newDays: number[]) => {
    if (!studentId) return false;

    try {
      // Delete existing schedule
      await supabase
        .from('student_activity_schedule')
        .delete()
        .eq('student_id', studentId)
        .eq('activity_id', activityId);

      // Insert new schedule
      if (newDays.length > 0) {
        const scheduleData = newDays.map(day => ({
          student_id: studentId,
          activity_id: activityId,
          day_of_week: day
        }));

        const { error } = await supabase
          .from('student_activity_schedule')
          .insert(scheduleData);

        if (error) throw error;
      }

      // Update local state
      setActivities(prev => 
        prev.map(activity => 
          activity.activityId === activityId 
            ? { ...activity, scheduledDays: newDays }
            : activity
        )
      );

      return true;
    } catch (error) {
      
      toast({
        title: "Error",
        description: "No se pudieron actualizar los días programados",
        variant: "destructive"
      });
      return false;
    }
  };

  const updateBillingTypeOverride = async (activityId: string, billingTypeOverride: 'per_session' | 'monthly' | null) => {
    if (!studentId) return false;

    try {
      const { error } = await supabase
        .from('student_activities')
        .update({ billing_type_override: billingTypeOverride })
        .eq('student_id', studentId)
        .eq('activity_id', activityId);

      if (error) throw error;

      // Update local state
      setActivities(prev => 
        prev.map(activity => 
          activity.activityId === activityId 
            ? { ...activity, billingTypeOverride }
            : activity
        )
      );

      toast({
        title: "Actualizado",
        description: "Tipo de facturación actualizado exitosamente",
      });

      return true;
    } catch (error) {
      
      toast({
        title: "Error",
        description: "No se pudo actualizar el tipo de facturación",
        variant: "destructive"
      });
      return false;
    }
  };

  useEffect(() => {
    loadStudentActivities();
  }, [studentId]);

  return {
    activities,
    loading,
    updateScheduledDays,
    updateBillingTypeOverride,
    refreshActivities: loadStudentActivities
  };
};
